import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search as SearchIcon, X, Loader2, BookOpen, Gamepad2, Globe, Trophy } from "lucide-react";
import { api } from "@/lib/api";
import Icon from "@/components/Icon";
import { Input } from "@/components/ui/input";

export default function Search() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!q.trim()) { setRes(null); return; }
    setBusy(true);
    const t = setTimeout(() => {
      api.get(`/search?q=${encodeURIComponent(q)}`).then((r) => setRes(r.data)).finally(() => setBusy(false));
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const hasResults = res && (res.lessons.length || res.simulations.length || res.categories.length || res.challenges.length);

  return (
    <div>
      <h1 className="font-display font-extrabold text-3xl tracking-tight mb-4">Search</h1>
      <div className="relative mb-6">
        <SearchIcon size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input data-testid="search-input" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search lessons, simulations, topics…" className="pl-11 pr-11 h-13 py-3 rounded-2xl bg-white/5 border-white/10 text-base" />
        {q && <button onClick={() => setQ("")} className="absolute right-4 top-1/2 -translate-y-1/2"><X size={18} className="text-muted-foreground" /></button>}
      </div>

      {busy && <div className="flex justify-center py-10"><Loader2 className="animate-spin text-indigo-400" size={28} /></div>}
      {res && !busy && !hasResults && <p className="text-center text-muted-foreground py-10">No results for "{q}"</p>}

      {res && !busy && (
        <div className="space-y-6">
          {res.categories.length > 0 && (
            <Section title="Worlds" icon={Globe}>
              {res.categories.map((c) => <Row key={c.key} title={c.name} sub={c.description} icon={c.icon} onClick={() => navigate(`/app/worlds/${c.key}`)} testid={`search-cat-${c.key}`} />)}
            </Section>
          )}
          {res.simulations.length > 0 && (
            <Section title="Simulations" icon={Gamepad2}>
              {res.simulations.map((s) => <Row key={s.key} title={s.title} sub={s.description} icon="gamepad-2" onClick={() => navigate(`/simulation/${s.key}`)} testid={`search-sim-${s.key}`} />)}
            </Section>
          )}
          {res.lessons.length > 0 && (
            <Section title="Lessons" icon={BookOpen}>
              {res.lessons.map((l) => <Row key={l.id} title={l.title} sub={l.category_key} icon="book" onClick={() => navigate(`/lesson/${l.id}`)} testid={`search-lesson-${l.id}`} />)}
            </Section>
          )}
          {res.challenges.length > 0 && (
            <Section title="Challenges" icon={Trophy}>
              {res.challenges.map((c) => <Row key={c.key} title={c.title} sub={c.desc} icon={c.icon} onClick={() => navigate("/app/challenges")} testid={`search-ch-${c.key}`} />)}
            </Section>
          )}
        </div>
      )}
    </div>
  );
}

const Section = ({ title, icon: I, children }) => (
  <div>
    <h2 className="font-display font-bold text-sm mb-2 flex items-center gap-2 text-muted-foreground"><I size={15} /> {title}</h2>
    <div className="space-y-2">{children}</div>
  </div>
);

const Row = ({ title, sub, icon, onClick, testid }) => (
  <button data-testid={testid} onClick={onClick} className="w-full flex items-center gap-3 glass rounded-2xl p-4 text-left hover:bg-white/5 transition-colors">
    <div className="h-10 w-10 rounded-xl bg-indigo-500/10 grid place-items-center shrink-0"><Icon name={icon} size={18} className="text-indigo-400" /></div>
    <div className="flex-1 min-w-0"><h3 className="font-semibold text-sm truncate">{title}</h3><p className="text-xs text-muted-foreground truncate capitalize">{sub}</p></div>
  </button>
);
